import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

// API
import { useParticipanteService } from '../../services/participanteServices';

// Estilos
import styles from './participantecadastropage.module.css';

const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log('[ParticipanteCadastroPage]', ...args); };

/**
 * Lê state.from e tenta inferir:
 * - turmaHash
 * - dest ('res' | 'ead')
 * Aceita /res/:hash, /responder/:hash e /ead/:hash
 */
function parseFrom(location) {
  const from = location?.state?.from || null;
  const pathname = from?.pathname || '';
  const search = from?.search || '';
  const hash = from?.hash || '';

  const match = pathname.match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const rawDest = match?.[1] || null;
  const turmaHash = match?.[2] || null;

  const dest = rawDest
    ? (rawDest.toLowerCase().startsWith('ead') ? 'ead' : 'res')
    : '';

  return { from, pathname, search, hash, turmaHash, dest };
}

/** Hook para ler ?turma=...&dest=... da query string */
function useQueryParams(location) {
  return useMemo(() => {
    try {
      const usp = new URLSearchParams(location.search || '');
      const turma = (usp.get('turma') || '').trim();
      const dest = (usp.get('dest') || '').trim().toLowerCase();
      return {
        turma: turma || '',
        dest: (dest === 'res' || dest === 'ead') ? dest : '',
      };
    } catch {
      return { turma: '', dest: '' };
    }
  }, [location.search]);
}

/**
 * Decide para onde voltar após cadastro+login:
 * 1) se tiver state.from.pathname -> volta pra lá
 * 2) senão, se tiver turma + dest (res|ead) -> volta /dest/turma
 * 3) senão -> /participante/page
 */
function getRedirectPath(location, turma, dest) {
  const fromPath = location?.state?.from?.pathname || '';
  if (fromPath) return fromPath;

  if (turma && (dest === 'res' || dest === 'ead')) {
    return `/${dest}/${encodeURIComponent(turma)}`;
  }

  return '/participante/page';
}

function ParticipanteCadastroPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const { loginParticipante } = useAuth();
  const { criarParticipante } = useParticipanteService();

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [senha, setSenha] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // Query: ?turma=...&dest=...
  const qp = useQueryParams(location);

  // State.from: /res/:hash ou /ead/:hash
  const fromInfo = useMemo(() => parseFrom(location), [location]);

  // Efetivos (prioriza query; depois state.from)
  const turmaHashEfetivo = useMemo(
    () => qp.turma || fromInfo.turmaHash || '',
    [qp.turma, fromInfo.turmaHash]
  );

  const destEfetivo = useMemo(
    () => qp.dest || fromInfo.dest || '',
    [qp.dest, fromInfo.dest]
  );

  const formatarCelular = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    const parte1 = numeros.slice(0, 2);
    const parte2 = numeros.slice(2, 7);
    const parte3 = numeros.slice(7, 11);

    if (numeros.length <= 2) return `(${parte1}`;
    if (numeros.length <= 7) return `(${parte1}) ${parte2}`;
    return `(${parte1}) ${parte2}-${parte3}`;
  };

  const handleCelularChange = (e) => {
    const input = e.target.value;
    const numeros = input.replace(/\D/g, '');
    setCelular(formatarCelular(numeros));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);
    setErro('');

    const celularSemMascara = celular.replace(/\D/g, '');

    if (celularSemMascara.length !== 11) {
      showToast('O celular deve conter 11 dígitos!', 3000, 'error');
      setErro('O celular deve conter 11 dígitos!');
      setCarregando(false);
      return;
    }

    // ✅ No seu fluxo (convite via /res ou /ead), turma deve existir para criar já vinculado
    if (!turmaHashEfetivo) {
      showToast(
        'Não foi possível identificar a turma. Acesse o link correto (convite) ou peça um novo.',
        5000,
        'error'
      );
      setCarregando(false);
      return;
    }

    const dados = {
      nome,
      email: email || '',
      celular: celularSemMascara,
      senha,
      loginPor: 'celular',
      idTurma: turmaHashEfetivo, // hash criptografado da turma
    };

    dbg('payload criarParticipante:', dados);

    try {
      const response = await criarParticipante(dados);
      dbg('criarParticipante response:', response);

      if (response?.success) {
        showToast('Cadastro realizado com sucesso!', 3000, 'success');

        // Login automático
        const loginResult = await loginParticipante(celularSemMascara, senha);
        dbg('loginParticipante result:', loginResult);

        if (loginResult?.success) {
          const destino = getRedirectPath(location, turmaHashEfetivo, destEfetivo || 'res');
          dbg('Cadastro+login OK -> navegando para:', destino);
          navigate(destino, { replace: true });
        } else {
          showToast('Erro ao fazer login automático.', 3000, 'error');
          setErro('Erro ao fazer login automático.');
        }
      } else {
        showToast(response?.message || 'Erro ao cadastrar participante', 4000, 'error');
        setErro(response?.message || 'Erro ao cadastrar participante');
      }
    } catch (error) {
      dbg('criarParticipante error:', error);
      showToast('Erro ao conectar com o servidor.', 4000, 'error');
      setErro('Erro ao conectar com o servidor.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!DEBUG) return;
    dbg('mount -> location:', location);
    dbg('mount -> fromInfo:', fromInfo);
    dbg('mount -> query turma/dest:', qp);
    dbg('mount -> turmaHashEfetivo:', turmaHashEfetivo);
    dbg('mount -> destEfetivo:', destEfetivo);
  }, [location, fromInfo, qp, turmaHashEfetivo, destEfetivo]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Cadastro de Participante</h2>

      {DEBUG && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <div><strong>from.pathname:</strong> {fromInfo.pathname || '(vazio)'}</div>
          <div><strong>query.turma:</strong> {qp.turma || '(vazio)'}</div>
          <div><strong>query.dest:</strong> {qp.dest || '(vazio)'}</div>
          <div><strong>from.turmaHash:</strong> {fromInfo.turmaHash || '(vazio)'}</div>
          <div><strong>from.dest:</strong> {fromInfo.dest || '(vazio)'}</div>
          <div><strong>efetivo.turma:</strong> {turmaHashEfetivo || '(vazio)'}</div>
          <div><strong>efetivo.dest:</strong> {destEfetivo || '(vazio)'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <input
          type="text"
          placeholder="Nome completo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={styles.input}
          required
        />

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={styles.input}
          required
        />

        <input
          type="text"
          placeholder="Celular (11) 91234-5678"
          value={celular}
          onChange={handleCelularChange}
          className={styles.input}
          required
        />

        <input
          type="password"
          placeholder="Senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className={styles.input}
          required
        />

        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? 'Cadastrando...' : 'Cadastrar'}
        </button>

        {erro && <p className={styles.error}>{erro}</p>}

        <p>Informe seus dados para que criemos seu registro!</p>
      </form>
    </div>
  );
}

export default ParticipanteCadastroPage;